import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["entries", "loader", "scrollAnchor", "tableContainer"]
  static values = { 
    page: Number,
    loading: Boolean,
    url: String,
    endReached: Boolean
  }
  
  connect() {
    this.pageValue = this.pageValue || 1
    this.loadingValue = false
    
    // Important: Do not override the endReachedValue if it was explicitly set via data attribute
    if (!this.hasEndReachedValue) {
      this.endReachedValue = false
    }
    
    // Create intersection observer for the scroll anchor
    this.createIntersectionObserver()
  }
  
  disconnect() {
    this.observer?.disconnect()
  }
  
  createIntersectionObserver() {
    this.observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        // If the scroll anchor is visible and we're not already loading
        // and we haven't reached the end, load more items
        if (entry.isIntersecting && !this.loadingValue && !this.endReachedValue) {
          this.loadMore()
        }
      })
    }, {
      // Use a smaller margin with the scrollable container
      rootMargin: '0px 0px 50px 0px',
      threshold: 0
    })
    
    if (this.hasScrollAnchorTarget) {
      this.observer.observe(this.scrollAnchorTarget)
    }
  }
  
  loadMore() {
    this.loadingValue = true
    
    if (this.hasLoaderTarget) {
      this.loaderTarget.classList.remove('d-none')
      this.loaderTarget.setAttribute('aria-busy', 'true')
    }
    
    // Scroll to the bottom of the table to show loading indicator
    if (this.hasTableContainerTarget) {
      setTimeout(() => {
        this.tableContainerTarget.scrollTop = this.tableContainerTarget.scrollHeight
      }, 50)
    }
    
    // Increment the page number
    const nextPage = this.pageValue + 1
    
    // Build the URL for the next page
    let baseUrl = this.urlValue || window.location.href
    
    // Ensure we have a full URL with protocol and host
    if (baseUrl.startsWith('/')) {
      baseUrl = window.location.origin + baseUrl
    }
    
    const url = new URL(baseUrl)
    url.searchParams.set('page', nextPage)
    
    // Fetch the next page
    fetch(url, {
      headers: {
        'Accept': 'text/html',
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
    .then(response => {
      if (response.status === 204) {
        this.endReachedValue = true
        return null
      }
      return response.text()
    })
    .then(html => {
      if (html && html.trim() && html.includes('<tr')) {
        // Check if there are actual rows in the response (look for tr elements)
        if (html.includes('<tr')) {
          // Insert the new HTML
          this.entriesTarget.insertAdjacentHTML('beforeend', html)
          
          // Make newly added rows keyboard navigable
          const newRows = this.entriesTarget.querySelectorAll('tr:not([tabindex="0"])')
          newRows.forEach(row => {
            row.setAttribute('tabindex', '0')
          })

          // Dispatch event for other controllers to know new rows were added
          document.dispatchEvent(new CustomEvent('infinite-scroll:rows-added', {
            detail: { page: nextPage }
          }))

          // Update the page value
          this.pageValue = nextPage
        } else {
          this.endReachedValue = true
        }

        // Re-observe the intersection after a delay
        setTimeout(() => {
          // Just reconnect the observer to ensure it continues tracking
          if (this.observer && this.hasScrollAnchorTarget) {
            this.observer.unobserve(this.scrollAnchorTarget)
            this.observer.observe(this.scrollAnchorTarget)
          }
        }, 100)
      } else {
        this.endReachedValue = true
      }
      
      this.loadingValue = false
      if (this.hasLoaderTarget) {
        this.loaderTarget.classList.add('d-none')
        this.loaderTarget.setAttribute('aria-busy', 'false')
      }
    })
    .catch(error => {
      console.error("Error loading more items:", error)
      this.loadingValue = false
      if (this.hasLoaderTarget) {
        this.loaderTarget.classList.add('d-none')
        this.loaderTarget.setAttribute('aria-busy', 'false')
      }
    })
  }
  
  // For test environment
  logState() {
    // Only used by tests, intentionally empty in production
  }
  
  // Called when page value changes - could be used for analytics
  pageValueChanged() {
    // Intentionally empty
  }
}